/**
 * EST3 WBS import column presets — Primavera P6, MS Project, and native EST3 templates.
 * Keep in sync with backend `wbs-import-mapping.ts`.
 */

export type Est3WbsImportPreset = 'auto' | 'est3' | 'primavera' | 'msproject';

export type WbsImportColumnMap = {
  code: string[];
  name: string[];
  parentCode?: string[];
  sortOrder?: string[];
  outlineLevel?: string[];
};

export const WBS_IMPORT_PRESET_LABELS: Record<Est3WbsImportPreset, string> = {
  auto: 'Auto-detect',
  est3: 'EST3 template',
  primavera: 'Primavera P6 (Excel export)',
  msproject: 'Microsoft Project (Excel export)',
};

export const WBS_IMPORT_PRESET_HINTS: Record<Est3WbsImportPreset, string> = {
  auto: 'Detects Primavera, MS Project, or EST3 columns from the header row.',
  est3: 'Code, Name, Parent Code, Sort Order',
  primavera: 'WBS Code, WBS Name, Parent WBS, Sequence Number',
  msproject: 'Outline Number, Task Name, Outline Level (parent derived from outline)',
};

export const WBS_IMPORT_PRESETS: Record<Exclude<Est3WbsImportPreset, 'auto'>, WbsImportColumnMap> = {
  est3: {
    code: ['code', 'wbs code', 'wbs_code'],
    name: ['name', 'title', 'description'],
    parentCode: ['parent code', 'parent_code', 'parent'],
    sortOrder: ['sort order', 'sort_order', 'sort'],
  },
  primavera: {
    code: ['wbs code', 'wbs id', 'wbs_code', 'wbs id/code', 'wbs'],
    name: ['wbs name', 'wbs description', 'wbs title', 'name'],
    parentCode: ['parent wbs', 'parent wbs code', 'parent wbs id', 'parent_wbs', 'parent'],
    sortOrder: ['sequence number', 'seq num', 'seq', 'sort order', 'sort'],
  },
  msproject: {
    code: ['outline number', 'outline #', 'wbs', 'wbs code'],
    name: ['task name', 'name', 'activity name', 'summary'],
    parentCode: ['parent wbs', 'parent outline number'],
    sortOrder: ['id', 'task id', 'task_id', 'line number'],
    outlineLevel: ['outline level', 'level', 'outline_level'],
  },
};

export type ResolvedWbsImportColumns = {
  preset: Exclude<Est3WbsImportPreset, 'auto'>;
  code: number;
  name: number;
  parentCode: number;
  sortOrder: number;
  outlineLevel: number;
};

export function normWbsImportHeader(h: string): string {
  return String(h || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function headerIndex(headerRow: string[], names: string[]): number {
  for (const n of names) {
    const i = headerRow.findIndex((h) => normWbsImportHeader(h) === normWbsImportHeader(n));
    if (i >= 0) return i;
  }
  return -1;
}

function scorePreset(headers: string[], map: WbsImportColumnMap): number {
  let score = 0;
  if (headerIndex(headers, map.code) >= 0) score += 2;
  if (headerIndex(headers, map.name) >= 0) score += 2;
  if (map.parentCode && headerIndex(headers, map.parentCode) >= 0) score += 2;
  if (map.sortOrder && headerIndex(headers, map.sortOrder) >= 0) score += 1;
  if (map.outlineLevel && headerIndex(headers, map.outlineLevel) >= 0) score += 3;
  return score;
}

function looksLikeActivityExport(headers: string[]): boolean {
  const hasActivityId = headerIndex(headers, ['activity id', 'activity_id']) >= 0;
  const hasActivityName = headerIndex(headers, ['activity name', 'activity_name']) >= 0;
  const hasWbsCode =
    headerIndex(headers, WBS_IMPORT_PRESETS.primavera.code) >= 0 ||
    headerIndex(headers, WBS_IMPORT_PRESETS.msproject.code) >= 0;
  return hasActivityId && hasActivityName && !hasWbsCode;
}

export function detectWbsImportPreset(headers: string[]): Exclude<Est3WbsImportPreset, 'auto'> {
  if (looksLikeActivityExport(headers)) {
    return 'est3';
  }
  const scores: Array<{ preset: Exclude<Est3WbsImportPreset, 'auto'>; score: number }> = [
    { preset: 'msproject', score: scorePreset(headers, WBS_IMPORT_PRESETS.msproject) },
    { preset: 'primavera', score: scorePreset(headers, WBS_IMPORT_PRESETS.primavera) },
    { preset: 'est3', score: scorePreset(headers, WBS_IMPORT_PRESETS.est3) },
  ];
  scores.sort((a, b) => b.score - a.score);
  if (scores[0].score > 0) return scores[0].preset;
  return 'est3';
}

export function resolveWbsImportColumns(
  headers: string[],
  preset: Est3WbsImportPreset = 'auto'
): ResolvedWbsImportColumns {
  const resolved = preset === 'auto' ? detectWbsImportPreset(headers) : preset;
  const map = WBS_IMPORT_PRESETS[resolved];
  return {
    preset: resolved,
    code: headerIndex(headers, map.code),
    name: headerIndex(headers, map.name),
    parentCode: map.parentCode ? headerIndex(headers, map.parentCode) : -1,
    sortOrder: map.sortOrder ? headerIndex(headers, map.sortOrder) : -1,
    outlineLevel: map.outlineLevel ? headerIndex(headers, map.outlineLevel) : -1,
  };
}

export function parentCodeFromOutline(outline: string): string | undefined {
  const trimmed = outline.trim();
  if (!trimmed) return undefined;
  const parts = trimmed.split('.').filter(Boolean);
  if (parts.length <= 1) return undefined;
  parts.pop();
  return parts.join('.');
}

export function normalizeWbsCode(code: string): string {
  return code.trim();
}
